import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { MomentDetail, MomentEdit } from "../api/types";
import { useServices } from "../app/services";
import { useAction } from "../app/useAction";
import { useResource } from "../app/useResource";
import { ConfirmAction } from "../components/cards";
import { Icon } from "../components/Icon";
import { Dialog, Field, PageHeader, Pill, ResourceView, Thumb } from "../components/ui";
import { kindLabel, performerNames } from "../lib/labels";
import { formatMs, parseTime, sourcePosition, speakMs } from "../lib/time";
import { watchUrl } from "../lib/youtube";

/**
 * ここ好きの編集（本人だけ）。
 * - 位置の元は、サーバー（音声ワーカー）が受付時に確定した位置。画面の推定位置で置きかえない
 * - VCで聞こえた瞬間とは通信の遅れの分だけずれるので、本人が ± で微調整できる（調整したことが分かるように、記録された位置と並べて出す）
 * - 下書きが指す曲・区間の版は、入力中に次の曲へ進んでも変わらない
 * - 「自分用に保存」と「メンバーに公開」は別の操作。公開は確認してから
 */
export function MomentEditPage() {
  const { id = "" } = useParams();
  const { api } = useServices();
  const res = useResource("moments.get", () => api.getMoment(id), [id]);
  return (
    <div className="page page--narrow">
      <ResourceView resource={res} what="ここ好き">
        {(d) => <MomentEditor key={d.moment.id} detail={d} reload={res.reload} />}
      </ResourceView>
    </div>
  );
}

const NUDGES = [-1000, -500, 500, 1000];

function MomentEditor({ detail, reload }: { detail: MomentDetail; reload: () => void }) {
  const { api, toasts } = useServices();
  const navigate = useNavigate();
  const { moment: m, performance: p, segment } = detail;
  // 区間の長さ。曲が分からないときは不明（範囲の確認はサーバーに任せる：moment_out_of_bounds）
  const length = segment.end_ms !== undefined ? segment.end_ms - segment.start_ms : null;
  // 新しい記録は受付時の位置を保持。旧データのnullは「いま保存されている位置」。
  const capturedKnown = typeof m.captured_position_ms === "number";
  const captured = m.captured_position_ms ?? m.position_ms;
  const [pos, setPos] = useState(m.position_ms);
  const [posText, setPosText] = useState(formatMs(m.position_ms, { tenths: true }));
  const [posError, setPosError] = useState<string | null>(null);
  const [useEnd, setUseEnd] = useState(m.end_ms !== null);
  const [endText, setEndText] = useState(m.end_ms !== null ? formatMs(m.end_ms, { tenths: true }) : "");
  const [endError, setEndError] = useState<string | null>(null);
  const [body, setBody] = useState(m.body);
  const [confirm, setConfirm] = useState<null | "publish" | "delete">(null);
  const isPublic = m.visibility === "members";

  const clamp = (v: number) => Math.max(0, length === null ? v : Math.min(length - 1, v));
  const nudge = (delta: number) => {
    const next = clamp(pos + delta);
    setPos(next);
    setPosText(formatMs(next, { tenths: true }));
    setPosError(null);
  };

  const readEdit = (): MomentEdit | null => {
    const start = parseTime(posText);
    let ok = true;
    if (start === null) {
      setPosError("「2:34」「2:34.5」のように入力してください。");
      ok = false;
    } else if (length !== null && start >= length) {
      setPosError(`区間の長さ（${formatMs(length)}）より前にしてください。`);
      ok = false;
    } else setPosError(null);
    let end: number | null = null;
    if (useEnd) {
      end = parseTime(endText);
      if (end === null) {
        setEndError("終わりの時刻を「2:49」のように入力してください。");
        ok = false;
      } else if (start !== null && end <= start) {
        setEndError("終わりは始まりより後にしてください。");
        ok = false;
      } else if (length !== null && end > length) {
        setEndError(`区間の長さ（${formatMs(length)}）までにしてください。`);
        ok = false;
      } else setEndError(null);
    } else setEndError(null);
    if (!ok || start === null) return null;
    return { position_ms: start, end_ms: end, body: body.trim(), visibility: "private" };
  };

  const savePrivate = useAction("moments.edit", (edit: MomentEdit) => api.editMoment(m.id, { ...edit, visibility: "private" }));
  const publish = useAction("moments.edit", (edit: MomentEdit) => api.publishMoment(m.id, { ...edit, visibility: "members" }));

  const onSavePrivate = async () => {
    const edit = readEdit();
    if (!edit) return;
    const r = await savePrivate.run(edit);
    if (r.ok) {
      toasts.show(`自分用に保存しました${savePrivate.suffix}`);
      reload();
    }
  };
  const onPublish = async () => {
    const edit = readEdit();
    setConfirm(null);
    if (!edit) return;
    const r = await publish.run(edit);
    if (r.ok) {
      toasts.show(`メンバーに公開しました${publish.suffix}`);
      if (r.value.post_id) void navigate(`/posts/${r.value.post_id}`, { replace: true });
      else {
        reload();
        void navigate("/library?tab=posts", { replace: true });
      }
    }
  };

  const delta = pos - captured;
  const source = sourcePosition(segment.start_ms, pos);

  return (
    <>
      <PageHeader title="ここ好きを整える" lead="時刻と一言を整えてから、自分用に保存するか、メンバーに公開するかを選びます。" />

      <section className="moment-target" aria-label="記録した曲">
        {p && segment.video_id ? <Thumb videoId={segment.video_id} title={p.work.title} size="s" /> : null}
        <div>
          {p ? (
            <>
              <p className="moment-target__title">
                <Link to={`/performances/${p.id}`}>{p.work.title}</Link>
              </p>
              <p className="moment-target__sub">
                {performerNames(p)} ・ {kindLabel(p.kind)}
                {segment.version !== undefined && <> ・ 区間 v{segment.version}</>}
              </p>
            </>
          ) : (
            <>
              <p className="moment-target__title">曲の情報を取得できません</p>
              <p className="moment-target__sub">
                APIのここ好きには曲の情報が含まれていないため、この画面では表示できません（区間の版 {m.segment_revision_id.slice(0, 8)}
                …）。時刻と一言の編集はできます。
              </p>
            </>
          )}
          <p className="moment-target__state">
            {isPublic ? (
              <Pill tone="calm" icon="check">
                メンバーに公開中
              </Pill>
            ) : m.visibility === "private" ? (
              <Pill tone="draft">自分だけ</Pill>
            ) : (
              <Pill tone="info">公開範囲を確認できません</Pill>
            )}
          </p>
        </div>
      </section>
      <p className="form__note">この下書きは、記録した時点の曲と区間の版を指しています。VCで次の曲に進んでも、対象は変わりません。</p>

      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void onSavePrivate();
        }}
        noValidate
      >
        <div className="captured">
          <p className="captured__label">{capturedKnown ? "サーバーが記録した位置" : "保存されている位置"}</p>
          <p className="captured__value">
            {formatMs(captured, { tenths: true })}
            <span className="muted"> ・ 元動画では {formatMs(sourcePosition(segment.start_ms, captured), { tenths: true })}</span>
          </p>
          <p className="captured__note">
            {capturedKnown
              ? "Botが音を送っている位置です。"
              : "記録したときは、サーバーがBotの音を送っている位置で決めました（画面の表示の位置は使っていません）。前に微調整して保存していれば、その位置です。"}
            あなたの端末で聞こえた瞬間とは、通信の遅れの分だけ少し差があります。
          </p>
        </div>

        <Field label="好きな瞬間（区間の始まりからの時刻）" hint="± で少しずつ合わせるか、直接入力します。" error={posError} required>
          {({ id, describedBy, invalid }) => (
            <div className="nudge">
              <input
                id={id}
                className="input input--time"
                inputMode="decimal"
                autoComplete="off"
                value={posText}
                onChange={(e) => {
                  setPosText(e.target.value);
                  const v = parseTime(e.target.value);
                  if (v !== null) setPos(clamp(v));
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
              <div className="nudge__buttons" role="group" aria-label="時刻の微調整">
                {NUDGES.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className="btn btn-secondary btn-small nudge__btn"
                    onClick={() => nudge(d)}
                    aria-label={`${Math.abs(d) / 1000}秒${d < 0 ? "前" : "後"}へ`}
                  >
                    {d < 0 ? "−" : "+"}
                    {Math.abs(d) / 1000}秒
                  </button>
                ))}
              </div>
            </div>
          )}
        </Field>
        <p className="nudge__summary" aria-live="polite">
          {delta === 0
            ? `${capturedKnown ? "記録された" : "保存されている"}位置のままです。`
            : `${capturedKnown ? "記録された" : "保存されている"}位置から ${delta > 0 ? "+" : "−"}${(Math.abs(delta) / 1000).toFixed(1)}秒`}
          （元動画では {formatMs(source, { tenths: true })}
          {segment.video_id && (
            <>
              ・
              <a href={watchUrl(segment.video_id, source)} target="_blank" rel="noopener noreferrer">
                YouTubeで確かめる
                <span className="visually-hidden">（新しいタブ・{speakMs(source)}から）</span>
              </a>
            </>
          )}
          ）
        </p>

        <label className="choice">
          <input type="checkbox" checked={useEnd} onChange={(e) => setUseEnd(e.target.checked)} />
          <span>終わりの時刻も付ける（範囲にする）</span>
        </label>
        {useEnd && (
          <Field label="終わり（区間の始まりからの時刻）" error={endError} required>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="input input--time"
                inputMode="decimal"
                autoComplete="off"
                value={endText}
                onChange={(e) => setEndText(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                placeholder={formatMs(length === null ? pos + 15000 : Math.min(length, pos + 15000))}
              />
            )}
          </Field>
        )}

        <Field label="ひとこと（任意）">
          {({ id }) => (
            <textarea
              id={id}
              className="input textarea"
              rows={3}
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="最後のサビのハモり！"
            />
          )}
        </Field>

        {(savePrivate.error || publish.error) && (
          <p className="inline-note inline-note--danger" role="alert">
            {savePrivate.error ?? publish.error}
          </p>
        )}
        {(!savePrivate.connected || !publish.connected) && (
          <p className="inline-note inline-note--warn">ここ好きの保存・公開は、まだAPIに接続されていません。</p>
        )}

        <div className="form__actions">
          <button
            type="submit"
            className="btn btn-secondary"
            disabled={savePrivate.running || !savePrivate.connected}
            aria-busy={savePrivate.running || undefined}
          >
            {isPublic ? "公開をやめて自分用にする" : "自分用に保存"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={publish.running || !publish.connected}
            aria-busy={publish.running || undefined}
            onClick={() => readEdit() && setConfirm("publish")}
          >
            {isPublic ? "公開の内容を更新…" : "メンバーに公開…"}
          </button>
        </div>
        <p className="form__danger">
          <button type="button" className="btn btn-ghost btn-small" onClick={() => setConfirm("delete")}>
            <Icon name="trash" className="btn-icon" />
            このここ好きを削除
          </button>
        </p>
      </form>

      <Dialog
        open={confirm === "publish"}
        title="メンバーに公開しますか？"
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(null)}>
              やめる
            </button>
            <button type="button" className="btn btn-primary" onClick={onPublish}>
              公開する
            </button>
          </>
        }
      >
        <p>
          「{p?.work.title ?? "この曲"}」の {formatMs(pos)}
          {useEnd && endText ? `〜${endText}` : ""}{" "}
          を、このサーバーのメンバーに公開します。曲のページの「聴きどころ」と、見つけるに表示されます。
        </p>
        <p className="muted">投稿ページはメンバー限定です。共有リンクのプレビューには本文・曲名・時刻が表示され、サーバー外からも取得できます。投稿者名やアイコンは表示しません。</p>
        <p className="muted">初回公開時は、投稿リンクをDiscordの共有チャンネルへ通知音・メンションなしで自動共有します。編集や再公開では再送しません。</p>
        <p className="muted">あとから「自分用にする」で公開をやめられます。共有先が保存したプレビューは、しばらく残る場合があります。</p>
      </Dialog>
      <ConfirmAction
        open={confirm === "delete"}
        title="このここ好きを削除しますか？"
        body={isPublic ? "公開も取り消されます。元に戻せません。" : "元に戻せません。"}
        confirmLabel="削除する"
        op="moments.delete"
        action={() => api.deleteMoment(m.id)}
        onClose={() => setConfirm(null)}
        onDone={() => navigate("/library?tab=moments")}
      />
    </>
  );
}
